<template>
  <div
    class="modal-top-section modal-top-section--align-center margin-bottom-0"
    v-if="!fromDetailTemplate"
  >
    <h3 class="modal-top-section__title">Configura el horario de atención</h3>
  </div>

  <div class="audio-description">
    <p class="description-subtitle align-left">Archivo de audio fuera de horario</p>
    <p class="modal-description align-left">
      Este archivo se reproducirá cuando el IVR se encuentre fuera del horario configurado.
    </p>
  </div>

  <a-form class="form" layout="vertical" :model="formState">
    <a-form-item name="file_audio">
      <ArchivePicker
        v-model:value="formState.out_of_schedule_file_audio"
        :supportedTypes="['.mp3']"
        :maxMBSize="3"
      />

      <AudioPreview
        v-if="formState.out_of_schedule_file_audio"
        :node-src="botDetails?.out_of_schedule_file_audio_url"
        :file="formState.out_of_schedule_file_audio"
      />
    </a-form-item>

    <a-form-item label="Seleccione los días de ejecución" name="daysSelecteds" class="day-picker">
      <GroupSelectedButtons
        ref="groupSelectedButtons"
        :dataButtons="WeekDays"
        :allSelecteds="false"
        :disabled="disabled"
        @buttonSelecteds="setDaysSelected"
        :selecteds="formState.daysSelected"
      />
    </a-form-item>

    <!-- <CustomScrollbar :height="fromDetailTemplate ? 'calc(100% - 170px)' : '250px'"> -->
    <CustomScrollbar :height="isCreateIvrModal ? '120px' : '240px'">
      <a-form-item
        v-if="formState.arrayRangePickers.length > 0"
        label="Seleccione los rangos horarios por día"
        name="arrayRangePickers"
        style="width: 100%"
      >
        <list-range-pickers
          ref="arrayRangePickers"
          :fromTreeSection="fromDetailTemplate"
          :data-source="sortDataSource(formState.arrayRangePickers).slice()"
          :disabled="disabled"
          @onChangeRange="handleChangeRange"
          @onAddRange="handleAddRange"
          @onRemoveRange="handleRemoveRange"
        />
      </a-form-item>
    </CustomScrollbar>
  </a-form>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import { ref, reactive, watch, computed } from "vue";
import { cloneDeep, isEqual } from "lodash";

import { Bot, Schedule } from "@/@types/ivr";
import { SchedulesForm, RawSchedule } from "@/@types/ivr/forms";
import { groupScheduleConfig } from "@/app/chatbots/utils/groupScheduleConfig";
import { transformIndexDays } from "@/app/shared/utils/transform";

import GroupSelectedButtons from "@/app/chatbots/components/buttons/GroupSelectedButtons.vue";
import ListRangePickers from "@/app/chatbots/components/organisms/ListRangePickers.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import ArchivePicker from "@/app/shared/components/molecules/ArchivePicker.vue";
import AudioPreview from "@/app/ivr/bot/components/buttons/AudioPreview.vue";

const WeekDays = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"];

const route = useRoute();

const isCreateIvrModal = computed(() => {
  const regExp = new RegExp("^/flows/ivr-automatic(?:/(\\d+))?$");
  return regExp.test(route.fullPath);
});

const props = defineProps<{
  fromDetailTemplate?: boolean;
  disabled?: boolean;
  botDetails?: Bot;
}>();

const emit = defineEmits<{
  (e: "toggle-activate-button", isDisabled: boolean): void;
}>();

const formState = reactive<SchedulesForm>({
  out_of_schedule_file_audio: props.botDetails?.out_of_schedule_file_audio || null,
  daysSelected: props.botDetails?.schedules
    ? groupScheduleConfig(
        props.botDetails?.schedules,
        (schedule: Schedule) => schedule?.weekday
      ).map((schedule) => schedule?.day)
    : [],

  arrayRangePickers: props.botDetails?.schedules
    ? groupScheduleConfig(props.botDetails?.schedules, (schedule: Schedule) => schedule?.weekday)
    : [],
});

const comparisonForm = cloneDeep(formState);

watch(formState, () => {
  const newForm = cloneDeep(formState);
  delete newForm.submitForm;

  const isDisabled = isEqual(newForm, comparisonForm);

  emit("toggle-activate-button", isDisabled);
});

const nameDay = (index: number) => {
  const days = transformIndexDays([index]);
  return days[0]?.day;
};

const sortDataSource = (dataSource: RawSchedule[]) => {
  const data = [...dataSource];
  return data.sort((a, b) => a.order - b.order);
};

const addDayToList = (day: number) => {
  const itemList = {
    order: day,
    day: day,
    nameDay: nameDay(day),
    hours: [
      {
        id: `range-${day}-1`,
        start: "",
        end: "",
      },
    ],
  };

  formState.arrayRangePickers.push(itemList);
};

const removeDayToList = (day: number) => {
  formState.arrayRangePickers = formState.arrayRangePickers.filter((item) => item.order !== day);
};

const setDaysSelected = (newDaysSelected: number[]) => {
  const noDaysSelected = formState.daysSelected.filter(
    (daySelected) => !newDaysSelected.includes(daySelected)
  );
  noDaysSelected.forEach((day) => {
    removeDayToList(day);
  });

  formState.daysSelected = newDaysSelected;
  formState.daysSelected.forEach((day) => {
    const data = [...formState.arrayRangePickers].find((item) => item.order === day);
    if (data === undefined) {
      addDayToList(day);
    }
  });
};

const handleChangeRange = (
  order: number,
  idRange: number | string,
  newRange: { start: string; end: string }
) => {
  const data = formState.arrayRangePickers.find((item) => {
    return item.order === order;
  });
  const range = data?.hours.find((hour) => {
    return hour.id == idRange;
  });

  if (!range) return;

  range.start = newRange.start;
  range.end = newRange.end;
};

const handleAddRange = (order: number) => {
  const day = formState.arrayRangePickers.find((item) => item.order === order);
  const newRange = {
    id: `range-${day?.day}-${(day?.hours?.length || 0) + 1}`,
    start: "",
    end: "",
  };

  if (day !== undefined) {
    day.hours.push(newRange);
  }
};

const handleRemoveRange = (order: number, keyRange: number | string) => {
  const dayObj = formState.arrayRangePickers.find((item) => item.order === order);

  if (!dayObj) return;

  dayObj.hours = dayObj.hours.filter((hour) => hour.id !== keyRange);
};

const arrayRangePickers = ref();
formState.submitForm = async () => {
  if (arrayRangePickers.value) {
    return await arrayRangePickers.value.handleSubmitRangesPromise();
  }
};

defineExpose({ formState });
</script>

<style scoped lang="sass">
h3
  color: $gray-9
  font-size: 20px
  margin-bottom: 16px

.form
  height: 100%

.day-picker
  :deep(label)
    font-weight: 600

.align-left
  text-align: left

.modal-description
  color: $gray-13

.margin-bottom-0
  margin-bottom: 0

.audio-description
  margin-top: 1em

.description-subtitle
  margin-bottom: 0
  font-weight: 600
  color: #262626E0
</style>
